import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { solveVrp, calculatePathDistance } from '../../../Services';
import { processVehiclePaths } from '../utils';

/**
 * Custom hook to manage VRP solving process
 */
export const useSolveVrp = (setPath) => {
  const [isSolving, setIsSolving] = useState(false);
  const [solveError, setSolveError] = useState('');
  const [solveResult, setSolveResult] = useState(null);

  // Auto solve path function
  const solvePath = async (locations, startId, selectedAlgorithm, maxCapacity) => {
    // Validate if start point is set
    if (!startId) {
      setSolveError('Please set a start point first');
      return;
    }
    
    // Check number of location points
    if (locations.length < 2) {
      setSolveError('At least 2 location points are needed to calculate path');
      return;
    }
    
    setSolveError('');
    setIsSolving(true);
    
    try {
      // Generate a unique group ID
      const groupId = `vrp_${uuidv4()}`;

      // Call VRP solving API, pass selected algorithm
      const result = await solveVrp(
        groupId, 
        locations, 
        startId, 
        selectedAlgorithm,
        true,  // Use GeoJSON format
        maxCapacity
      );

      // Process the result data using utility function
      const {
        processedPath,
        pathMetadata,
        processedResult
      } = processVehiclePaths(result);
      
      // Set path with metadata if available
      if (pathMetadata) {
        setPath(processedPath, pathMetadata);
      } else {
        setPath(processedPath);
      }
      
      // Set solve result
      setSolveResult(processedResult);

    } catch (error) {
      console.error('VRP solving failed:', error);
      // Display detailed error information
      const errorMessage = error.message || 'Error solving VRP';
      console.error('Error details:', error);
      setSolveError(`Error: ${errorMessage}`);
    } finally {
      setIsSolving(false);
    }
  };

  // Update solve result from path data
  const updateSolveResult = (path, locations, selectedAlgorithm, maxCapacity) => {
    if (path && path.length > 1 && locations && locations.length > 0) {
      // Calculate distance using imported service function
      const distance = calculatePathDistance(path, locations);
      // Estimate time (assuming average speed of 40km/h)
      const avgSpeed = 40; // km/h
      const time = distance / avgSpeed * 60; // Convert to minutes
      
      // Get vehicle path data (if exists)
      const vehiclePaths = path.vehiclePaths || [];
      
      setSolveResult({
        path: path,
        distance: distance,
        time: time,
        algorithm: selectedAlgorithm,
        vehiclePaths: vehiclePaths,
        maxCapacity: maxCapacity,
        vehicleCount: vehiclePaths.length || 0 // Use vehicle count returned from API
      });
    } else {
      setSolveResult(null);
    }
  };

  return {
    isSolving,
    solveError,
    solveResult,
    setSolveResult,
    solvePath,
    updateSolveResult
  };
}; 